import React, { useCallback, useContext } from 'react';
import moment from 'moment';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { isCellClearable, isCellEditable, isReadOnly } from 'shared/utils/timesheet';
import BaseTimeRenderer from './BaseTimeRenderer';

const CheckInTimeRenderer = ({
  rowData,
  columnData: {
    updateCheckInTime,
    updateOSCheckInTime,
    updateOSCheckOutTime,
    updateCheckOutTime,
    allowUpdateExternal = false,
    readOnly = false,
    onOutsideClickSave = false,
    disableWarning = false
  } = {}
}) => {
  const myPrivileges = useContext(PrivilegesContext);
  const formattingMap = useFormattingMap();

  const onChangeCheckOutTime = useCallback(
    ({ endTime }) => {
      const updateFunc = !rowData.isExternal ? updateCheckOutTime : updateOSCheckOutTime;
      if (!updateFunc) return;
      updateFunc({ newTime: endTime, ...rowData });
    },
    [rowData, updateCheckOutTime, updateOSCheckOutTime]
  );

  const onChangeCheckInTime = useCallback(
    ({ startTime, endTime }) => {
      if (
        endTime &&
        !moment(endTime, API_DATE_TIME_FORMAT).isSame(moment(rowData.actualCheckOut, API_DATE_TIME_FORMAT))
      ) {
        onChangeCheckOutTime({ endTime });
      }
      return !rowData.isExternal
        ? updateCheckInTime({ newTime: startTime, ...rowData })
        : updateOSCheckInTime({ newTime: startTime, ...rowData });
    },
    [onChangeCheckOutTime, rowData, updateCheckInTime, updateOSCheckInTime]
  );

  const handleUpdateTimeSheet = useCallback(
    (newTime, originalNewTime) => {
      const { startTime } = getTimeRangeWithDates({ date: rowData.date, startTime: newTime, checkBaseStartDate: true });

      let endTime = rowData.actualCheckOut;
      if (endTime) {
        const timeValues = getTimeRangeWithDates({
          date: rowData.date,
          startTime,
          endTime: rowData.actualCheckOut,
          checkBaseStartDate: true,
          includeEqualStartEndTIme: false
        });

        endTime = timeValues.endTime;
      }

      if (newTime) {
        if (!moment(startTime, API_DATE_TIME_FORMAT).isSame(moment(rowData.actualCheckIn, API_DATE_TIME_FORMAT))) {
          onChangeCheckInTime({ startTime, endTime });
        }
      } else if (originalNewTime === null) {
        onChangeCheckInTime({ startTime, endTime });
      }
    },
    [rowData.date, rowData.actualCheckOut, rowData.actualCheckIn, onChangeCheckInTime]
  );

  const isEditable = isCellEditable({
    readOnly: isReadOnly(rowData, readOnly),
    updateFunc: rowData?.isExternal ? updateOSCheckInTime : updateCheckInTime,
    isExternal: rowData?.isExternal,
    allowUpdateExternal
  });

  const isClearable = isCellClearable({
    isClientApproved: rowData?.clientApproved && rowData?.isExternal,
    isApproved: rowData?.approved,
    isUnlockPayrollPrivilege: myPrivileges[privileges.UNLOCK_PAYROLL],
    // this cell is clearable if the check out time does not have value
    allowClear: !rowData.actualCheckOut
  });

  return (
    <>
      <BaseTimeRenderer
        isIn
        disableWarning={disableWarning}
        tooltipBody={rowData.actualCheckIn && formattingMap.dateAndTime(rowData.actualCheckIn)}
        rowData={rowData}
        actualValue={rowData.actualCheckIn}
        actualUserValue={rowData.userActualCheckIn}
        actualReporter={rowData.actualCheckInReporter}
        reportMethod={rowData.reportMethods?.checkIn}
        reportedValueMethod={rowData.reportMethods?.reportedCheckIn}
        scheduleValue={rowData.scheduleIn}
        onChangeActualValue={handleUpdateTimeSheet}
        isEditable={isEditable}
        onOutsideClickSave={onOutsideClickSave}
        clearable={isClearable}
      />
    </>
  );
};

export default CheckInTimeRenderer;
